import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import JoditEditor from "jodit-react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import ProgressBar from "react-bootstrap/ProgressBar";
import "bootstrap-icons/font/bootstrap-icons.css";

const CreatePage = () => {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("draft");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const editor = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const fileInputRef = useRef(null);

  const config = useMemo(
    () => ({
      readonly: false,
      height: 500,
      extraButtons: [
        {
          name: "customButton",
          icon: "video",
          tooltip: "Insert Video",
          exec: (editorInstance) => {
            setShowVideoModal(true);
          },
        },
      ],
    }),
    []
  );

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  useEffect(() => {
    if (title) {
      setSlug(generateSlug(title));
    }
  }, [title]);

  useEffect(() => {
    if (showVideoModal) {
      loadVideos();
    }
  }, [showVideoModal]);

  const loadVideos = async () => {
    setIsLoadingVideos(true);
    setVideoError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/vimeo-videos`
      );

      if (response.data.success) {
        setVideos(response.data.videos);
        if (response.data.videos.length === 0) {
          setVideoError("No videos available");
        }
      } else {
        setVideoError("Unable to fetch videos");
        console.error("Error loading videos:", response.data.message);
      }
    } catch (error) {
      setVideoError("Unable to fetch videos");
      console.error("Error loading videos:", error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pages`,
        {
          title,
          slug,
          content,
          status,
        }
      );
      alert("Page created successfully");
      console.log(response.data);
    } catch (error) {
      console.error(error);
      alert("Failed to create page");
    }
  };

  const insertVideo = (embedCode) => {
    if (editor.current) {
      editor.current.selection.insertHTML(embedCode);
    }
    setShowVideoModal(false);
  };
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setUploadError(null);
  };

  const initiateUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file first");
      return;
    }

    try {
      setUploadProgress(0);
      setUploadError(null);

      // Step 1: Request upload ticket from your backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pages/initiate-video-upload`,
        {
          title: uploadTitle || selectedFile.name,
          file_size: selectedFile.size,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to initiate upload");
      }

      const { upload_link, vimeo_id } = response.data.data;

      // Step 2: Perform the actual TUS upload
      const tus = await import("tus-js-client");
      const upload = new tus.Upload(selectedFile, {
        endpoint: upload_link,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        metadata: {
          filename: selectedFile.name,
          filetype: selectedFile.type,
        },
        onError: (error) => {
          setUploadError(`Upload failed: ${error.message}`);
          console.error("Upload error:", error);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const progress = (bytesUploaded / bytesTotal) * 100;
          setUploadProgress(progress);
        },
        onSuccess: async () => {
          // Step 3: Finalize the upload with your backend
          try {
            await finalizeUpload(vimeo_id);
            setShowUploadModal(false);
            loadVideos(); // Refresh the video list
          } catch (error) {
            setUploadError(`Finalization failed: ${error.message}`);
          }
        },
      });

      upload.start();
    } catch (error) {
      setUploadError(error.message);
      console.error("Upload initiation error:", error);
    }
  };

  const finalizeUpload = async (vimeoId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/pages/finalize-video-upload/${vimeoId}`,
        {
          title: uploadTitle || selectedFile.name,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to finalize upload");
      }

      return response.data;
    } catch (error) {
      console.error("Finalization error:", error);
      throw error;
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Add a Page</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>Slug</label>
          <input
            type="text"
            className="form-control"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
          />
        </div>

        <div className="form-group position-relative">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label>Content</label>
          </div>
          <JoditEditor
            value={content}
            ref={editor}
            config={config}
            onBlur={(newContent) => setContent(newContent)}
            onEditor={(editorInstance) => {
              editor.current = editorInstance;
            }}
          />
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            className="form-control"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        <button type="submit" className="btn btn-primary mt-3">
          Create Page
        </button>
      </form>

      {/* Video Gallery Modal */}
      <Modal
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Insert Video</Modal.Title>
        </Modal.Header>
        <Modal.Body>{/* ... existing modal body content ... */}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowUploadModal(true);
              setShowVideoModal(false);
            }}
          >
            <i className="bi bi-upload me-1"></i> Upload Video
          </Button>
          <Button variant="secondary" onClick={() => setShowVideoModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Upload Video Modal */}
      <Modal
        show={showUploadModal}
        onHide={() => {
          setShowUploadModal(false);
          setShowVideoModal(true);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload Video</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Video Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter video title"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Video File</Form.Label>
              <Form.Control
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="video/*"
              />
              <Form.Text className="text-muted">
                Supported formats: MP4, MOV, AVI, etc.
              </Form.Text>
            </Form.Group>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mb-3">
                <ProgressBar
                  now={uploadProgress}
                  label={`${Math.round(uploadProgress)}%`}
                />
                <div className="text-center mt-2">
                  Uploading... Please don't close this window.
                </div>
              </div>
            )}

            {uploadError && (
              <div className="alert alert-danger">{uploadError}</div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowUploadModal(false);
              setShowVideoModal(true);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={initiateUpload}
            disabled={!selectedFile || uploadProgress > 0}
          >
            {uploadProgress > 0 ? "Uploading..." : "Start Upload"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CreatePage;
